/**
 * "Found fourteen things on that receipt — which of these are right?"
 *
 * The same bargain as the shelf-scan review, for higher stakes. A shelf photo
 * offers three or four items; a receipt offers a whole shop, and a model that
 * mistakes a multibuy discount for a product would put "SAVE 50P" in someone's
 * fridge fourteen items deep, where nobody would ever find it again.
 *
 * Two things it has that the shelf review doesn't:
 *
 * - **A date.** The receipt knows when the shop happened, which is the entire
 *   reason backdating exists. When it couldn't read one, the field falls back
 *   to today and *says so* rather than quietly pretending.
 * - **Quantities.** A receipt states how much, which a photograph never could.
 *
 * And one thing it deliberately lacks: the photo. The shelf review passes its
 * picture through to be attached to each item; a receipt image is 400KB, has no
 * later use, and carries the store, the time and a card's last four digits. It
 * is sent, read, and dropped.
 */

import { useState } from "react";

import { CATEGORY_RULES } from "../features/food/categories";
import { FOOD_CATEGORIES } from "../features/food/types";
import type { FoodCategory, Unit } from "../features/food/types";
import type { ReceiptRead } from "../features/receipts/types";
import { CATEGORY_ICONS, Icon } from "./Icon";
import { Sheet } from "./Sheet";
import { UnitToggle } from "./UnitToggle";

import "./ScanReview.css";
import "./ReceiptReview.css";

export interface ReceiptRow {
  keep: boolean;
  name: string;
  category: FoodCategory;
  quantity: string;
  unit: Unit;
  shelfLifeDays: number;
}

interface ReceiptReviewProps {
  read: ReceiptRead;
  /** Today, so the date field has a sane fallback and a sane maximum. */
  today: string;
  onConfirm: (rows: ReceiptRow[], purchaseDate: string) => void;
  onClose: () => void;
}

export function ReceiptReview({ read, today, onConfirm, onClose }: ReceiptReviewProps) {
  const [rows, setRows] = useState<ReceiptRow[]>(() =>
    read.lines.map((line) => ({
      keep: true,
      name: line.name,
      category: line.category,
      quantity: String(line.quantity),
      unit: line.unit,
      shelfLifeDays: line.shelfLifeDays,
    }))
  );
  const [purchaseDate, setPurchaseDate] = useState(read.purchaseDate ?? today);

  const update = (index: number, patch: Partial<ReceiptRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const keeping = rows.filter((row) => row.keep);
  const blank = keeping.some((row) => row.name.trim().length === 0);
  const badQuantity = keeping.some((row) => {
    const value = Number(row.quantity);
    return !row.quantity.trim() || !Number.isFinite(value) || value <= 0;
  });
  const badDate = !purchaseDate || purchaseDate > today;

  return (
    <Sheet title={`Found ${read.lines.length}`} icon="camera" onClose={onClose}>
      <p className="scan__intro">
        Untick anything that isn't food, and fix anything it got wrong.
      </p>

      <label className="receipt__date">
        <span className="field__label">Bought</span>
        <input
          className="field__input"
          type="date"
          value={purchaseDate}
          max={today}
          onChange={(event) => setPurchaseDate(event.target.value)}
          aria-invalid={badDate}
        />
      </label>

      {!read.purchaseDate && (
        <p className="receipt__note">
          <Icon name="alert" className="receipt__note-icon" />
          Couldn't read a date on the receipt — this is today. Change it if the
          shop was another day.
        </p>
      )}

      <ul className="scan__rows">
        {rows.map((row, index) => (
          <li className="scan__row" key={index} data-dropped={!row.keep || undefined}>
            <button
              className="scan__keep"
              type="button"
              aria-pressed={row.keep}
              aria-label={`Keep ${row.name || "this item"}`}
              onClick={() => update(index, { keep: !row.keep })}
            >
              <Icon name={row.keep ? "check" : "close"} className="scan__keep-icon" />
            </button>

            <span className="scan__fields">
              <input
                className="scan__name"
                value={row.name}
                aria-label="Name"
                disabled={!row.keep}
                onChange={(event) => update(index, { name: event.target.value })}
              />

              <span className="receipt__amount">
                <input
                  className="receipt__quantity"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={row.quantity}
                  aria-label="Amount"
                  disabled={!row.keep}
                  onChange={(event) => update(index, { quantity: event.target.value })}
                />
                <UnitToggle
                  value={row.unit}
                  disabled={!row.keep}
                  onChange={(unit) => update(index, { unit })}
                />
              </span>

              <span className="scan__category">
                <Icon
                  name={CATEGORY_ICONS[row.category]}
                  className="scan__category-icon"
                  data-category={row.category}
                />
                <select
                  className="scan__select"
                  value={row.category}
                  aria-label="Category"
                  disabled={!row.keep}
                  onChange={(event) =>
                    update(index, { category: event.target.value as FoodCategory })
                  }
                >
                  {FOOD_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {CATEGORY_RULES[category].label}
                    </option>
                  ))}
                </select>
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/*
        The row the model missed.

        A receipt read is never complete — a faint line, a wrapped name, an item
        the model dropped. Without this the only way to add it is to finish the
        review, go back to the picker and type it in from scratch, by which
        point you have lost the receipt in your hand. One button here keeps the
        correction next to the thing being corrected.
      */}
      <button
        className="scan__add-row"
        type="button"
        onClick={() =>
          setRows((current) => [
            ...current,
            {
              keep: true,
              name: "",
              category: "other",
              quantity: "1",
              unit: "pcs",
              shelfLifeDays: 7,
            },
          ])
        }
      >
        <Icon name="plus" className="scan__add-row-icon" />
        Add a missing item
      </button>

      {(blank || badQuantity) && (
        <p className="scan__error" role="alert">
          <Icon name="alert" className="scan__error-icon" />
          {blank ? "Every kept item needs a name." : "Every amount must be above 0."}
        </p>
      )}

      {/* Said once, plainly, on the screen where it's true. */}
      <p className="receipt__privacy">
        The photo was sent to read it and isn't kept. Your receipt has a shop, a
        time and often the last four digits of a card on it; none of that is
        stored here.
      </p>

      <div className="scan__actions">
        <button className="scan__cancel" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="scan__confirm"
          type="button"
          disabled={keeping.length === 0 || blank || badQuantity || badDate}
          onClick={() => onConfirm(keeping, purchaseDate)}
        >
          {keeping.length === 0
            ? "Nothing selected"
            : `Add ${keeping.length} item${keeping.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </Sheet>
  );
}
